import styles from './styles.module.css'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { useEffect, useState } from 'react'
import { Tag } from 'primereact/tag'
import { Dialog } from 'primereact/dialog'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { getAgentProjectsRequest } from '../../services/wisdomServices/projectService'
import { cancelManualTimeRequest, cancelOvertimeRequest, createManualTimeRequest, createOvertimeRequest, deleteRejectedManualTimeRequestRequest, deleteRejectedOvertimeRequestRequest } from '../../services/wisdomServices/requestService'
import DatePicker from '../datePicker/DatePicker'
import { getSessionItem } from '../../utils/helper/session'
import { Message } from 'primereact/message'
import { InputText } from 'primereact/inputtext'
import { Can, PERMISSIONS } from '../../security'

/**
 * List of predefined reasons for Manual Time requests.
 */
const manualTimeReasonsLists = [
    { id: 1, name: "Tracking Tool Lost Time", needExplanation: true },
    { id: 3, name: "Working on Linux", needExplanation: false },
    { id: 4, name: "Forgot to turn on tracker", needExplanation: false },
    { id: 5, name: "Other", needExplanation: true },
];

/**
 * List of predefined reasons for Overtime requests.
 */
const overtimeReasonsLists = [
    { id: 1, name: "Planned Extended shift" },
    { id: 2, name: "Planned Weekend shift" },
    { id: 3, name: "Requested by the customer" },
    { id: 4, name: "Not planned additional hours" },
    { id: 5, name: "Other" },
];

/**
 * Datatable component responsible for displaying time requests
 * (Manual Time and Overtime) and handling creation, cancellation
 * and deletion of those requests.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.header - Table header content.
 * @param {Array<Object>} props.data - Data source for the table.
 * @param {React.ReactNode} props.footer - Table footer content.
 * @param {Object} props.filters - DataTable filter configuration.
 * @param {Array<string>} props.globalFilterFields - Fields used in global search.
 * @param {Function} props.onSubmitted - Callback triggered after any request mutation.
 * @param {Function} props.onDateSelect - Callback triggered when month/year is selected.
 *
 * @returns {JSX.Element}
 */
function Datatable({header, data, footer, filters, globalFilterFields, onSubmitted, onDateSelect, loading}){
    const user = getSessionItem("User")
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const [selectedReason, setSelectedReason] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [requestManualTimeDialog, setRequestManualTimeDialog] = useState(false);
    const [requestOvertimeDialog, setRequestOvertimeDialog] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [explanationRequired, setExplanationRequired] = useState(false);
    const [reasonExplanation, setReasonExplanation] = useState(null);
    const [loadingSubmittRequest, setLoadingSubmittRequest] = useState(false);

    const isFormValid = () => {
        return Boolean(
            startDate &&
            endDate &&
            selectedProject &&
            selectedReason
        );
    };
    
    /**
     * Maps request status to PrimeReact Tag severity.
     *
     * @param {string} status - Request status.
     * @returns {string} PrimeReact severity value.
     */
    const getStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'info';
            
            case 'accepted':
                return 'success'

            case 'rejected':
                return 'danger'

            case 'cancelled':
                return 'warning'
        }
    }

    /**
     * Maps request type to PrimeReact Tag severity.
     *
     * @param {string} type - Request type.
     * @returns {string} PrimeReact severity value.
     */
    const getType = (type) => {
        switch (type) {
            case 'overtime':
                return 'info';
            
            case 'manualTime':
                return 'success'
        }
    }

    /**
     * Cancels a pending request based on its type.
     *
     * @async
     * @param {Object} rowData - Row data from DataTable.
     */
    const handleCancellRequest = async(rowData) => {
        try {
            rowData.type === 'manualTime'? await cancelManualTimeRequest(user.id, rowData.id) : await cancelOvertimeRequest(user.id, rowData.id)
        } catch (error) {
            console.error(error)

        } finally{
            onSubmitted()
        }
    }

    /**
     * Deletes a rejected or cancelled request.
     *
     * @async
     * @param {Object} rowData - Row data from DataTable.
     */
    const handleDeleteRequest = async(rowData) => {
        try {
            rowData.type === 'manualTime'? await deleteRejectedManualTimeRequestRequest(user.id, rowData.Date, rowData.ProjectID) : await deleteRejectedOvertimeRequestRequest(user.id, rowData.Date, rowData.ProjectID)
        } catch (error) {
            console.error(error)

        } finally{
            onSubmitted()
        }
    }

    /**
     * Renders the Status column content.
     *
     * @param {Object} rowData - Row data from DataTable.
     * @returns {JSX.Element}
     */
    const statusBodyTemplate = (rowData) => {
        if(rowData.status === 'pending'){
            return (
                <div className={styles.tableActionBtns}>
                    <Can permission={PERMISSIONS.requestsManualTimeUsersByUseridByRequestid_patch}>
                        <Tag value={rowData.status} severity={getStatus(rowData.status)} />
                    </Can>
                    <Can permission={PERMISSIONS.requestsManualTimeUsersByUseridByRequestidPending_delete}>
                        <Tag value="Cancel" severity={getStatus("cancelled")} onClick={() => handleCancellRequest(rowData)}/>
                    </Can>
                    
                </div>
            )

        }else if(rowData.status === 'cancelled'){
            return <Can permission={PERMISSIONS.requestsManualTimeUsersByUseridByRequestid_delete}><Tag value={rowData.status} severity={getStatus(rowData.status)} onClick={() => handleDeleteRequest(rowData)}/></Can>
        }
        else{
            return <Tag value={rowData.status} severity={getStatus(rowData.status)} />;

        }
    }

    /**
     * Renders the Type column content.
     *
     * @param {Object} rowData - Row data from DataTable.
     * @returns {JSX.Element}
     */
    const typeBodyTemplate = (rowData) => {
        return <Tag value={rowData.type} severity={getType(rowData.type)} />;
    }

     /**
     * Triggers data reload based on selected month and year.
     */
    const handleFindRequests = async() => {
        onDateSelect(selectedMonth, selectedYear)
    }

    /**
     * Handles month selection change.
     *
     * @param {number} month
     */
    const handleMonthChange = async(month) => {
        setSelectedMonth(month)
    }

    /**
     * Handles year selection change.
     *
     * @param {number} year
     */
    const handleYearChange = async(year) => {
        setSelectedYear(year)
    }

    /**
     * Toolbar template rendered on the right side.
     *
     * @returns {JSX.Element}
     */
    const rightToolbarTemplate = () => {
        return (
            <div className={styles.headerActions}>
                <Button 
                    label='Request Manual Time' 
                    icon="pi pi-plus" 
                    id="request-manual-time-button"  
                    disabled={loading} 
                    onClick={() => setRequestManualTimeDialog(true)} severity="success" 
                />
                <Button 
                    label="Request Overtime" 
                    icon="pi pi-plus"
                    id="request-overtime-button"  
                    disabled={loading} 
                    onClick={() => setRequestOvertimeDialog(true)} 
                    severity="info" 
                />
            </div>
        )
    }

    /**
     * Toolbar template rendered on the left side.
     *
     * @returns {JSX.Element}
     */
    const leftToolbarTemplate = () => {
        return (
            <div className={styles.headerActions}>
                <DatePicker onMonthChange={handleMonthChange} onYearChange={handleYearChange}/>
                <Button label='Search' icon="pi pi-check" severity='success' loading={loading} onClick={handleFindRequests} style={{borderRadius:"7px"}}/>

            </div>
        )
    }

    /**
     * Resets dialog state and form submission flags.
     */
    const hideDialog = () => {
        setSelectedProject(null);
        setSelectedReason(null);
        setStartDate(null);
        setEndDate(null);
        setExplanationRequired(false);
        setReasonExplanation(null);
        setRequestManualTimeDialog(false);
        setRequestOvertimeDialog(false);
    };

    /**
     * Creates a Manual Time or Overtime request.
     *
     * @async
     * @param {'manualTime'|'overtime'} type - Request type.
     */
    const saveRequest = async(type) => {
        try {
            setLoadingSubmittRequest(true)
            if (!startDate || !endDate || !selectedProject || !selectedReason) {
                return;
            }
    
            let payload = {
                projectId: selectedProject.id,
                from: startDate?.toISOString(),
                to: endDate?.toISOString()
            };

            if(explanationRequired){
                payload.reason = `${selectedReason} - ${reasonExplanation}`
            }else{
                payload.reason = selectedReason

            }
    
            type === 'manualTime'? await createManualTimeRequest(payload) : await createOvertimeRequest(payload)
            
            
        } catch (error) {
            console.error(error)

        } finally{
            setLoadingSubmittRequest(false)
            hideDialog()
            onSubmitted()
        }
    }

    const requestManualTimeDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog}/>
                <Button label="Save" icon="pi pi-check" loading={loadingSubmittRequest} disabled={!isFormValid() || (explanationRequired && !reasonExplanation)} outlined onClick={() => saveRequest('manualTime')}/>
        </>
    )

    const requestOvertimeDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog}/>
            <Button label="Save" icon="pi pi-check" loading={loadingSubmittRequest} disabled={!isFormValid()} outlined onClick={() => saveRequest('overtime')}/>
        </>
    )

    const handleManualTimeRequestReason = (e) => {
        const selected = manualTimeReasonsLists.find(x => x.name === e.value);
        setSelectedReason(e.value);
        setExplanationRequired(selected?.needExplanation || false);

        if (!selected?.needExplanation) {
            setReasonExplanation(null);
        }
    }

    /**
     * Fetches projects associated with the current agent.
     *
     * @async
     */
    const getProjects = async() => {
        try {
            let projects = await getAgentProjectsRequest(user.id, false, false, 1, 100)
            setProjects(projects.data)
            
        } catch (error) {
            console.error(error)
        }
    }

    const manualTimeMessage = (
        <>
            This feature allows you to add workhours to your timesheets.
            <br/>For instance, if you forgot to turn on the tracker or if you have an OS that is not supported. 
            <br/><strong>MT can only be entered 7 days in the past</strong>, and please select the time based on your timezone. 
            <br/>If you have any questions, please reach out to your manager.
        </>
    )

    const overtimeMessage = (
        <>
            This feature will allow you to extend your project weekly hour cap.
            <br/>It will extend the maximum hours counted toward your payroll time.
            <br/>It will not add time to your total time. 
            <br/>Example: Cap time is 40 hrs, with OT of 4 hrs, your maximum payroll time will be 44hr.
            <br/>Please be mindful that those additional 4 hrs still need to be tracked.
            <br/>Please select time according to your timezone.
        </>
    )

    useEffect(() => {
        getProjects()
    }, [])

    return(
        <div>
            <div className='card'>
                <Toolbar start={leftToolbarTemplate} end={rightToolbarTemplate}/>
                <DataTable value={data} paginator rows={10} dataKey="id" filters={filters} filterDisplay="row" loading={loading}
                    globalFilterFields={globalFilterFields} header={header} footer={footer} emptyMessage="No data found."
                >
                    <Column field='Date' header='Date'/>
                    <Column field='type' header='Type' body={typeBodyTemplate}/>
                    <Column field='Project' header='Project'/>
                    <Column field='startTime' header='Start Time'/>
                    <Column field='endTime' header='End Time'/>
                    <Column field='Hours' header='Hours'/>
                    <Column field='reason' header='Reason'/>
                    <Column field='Manager' header='Approver'/>
                    <Column field='approvalDate' header='Approval Date'/>
                    <Column field='rejectReason' header='Reject Reason'/>
                    <Column field='status' header='Status' body={statusBodyTemplate}/>
                </DataTable>
            </div>

            {/* MANUAL TIME DIALOG */}
            <Dialog 
                dismissableMask
                visible={requestManualTimeDialog}
                onHide={hideDialog}
                footer={requestManualTimeDialogFooter}
            >
                <div>
                    <h1>Manual Time Request</h1>
                    <Message style={{marginBottom:"8px"}} severity='info' text={manualTimeMessage}/>
                    <div className={styles.requestsForm}>
                        <div className={styles.requestsTimeSelectorContainer}>
                            <div className={styles.requestsTimeSelector}> 
                                <Calendar
                                    tooltip='Select the start date'
                                    className={styles.requestCalendars} 
                                    minDate={new Date(new Date().setDate(new Date().getDate() - 7))}
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.value)} 
                                    showTime 
                                    stepMinute={10}
                                    hourFormat='12' 
                                    placeholder='From Date'
                                />
                            </div>
                            <div className={styles.requestsTimeSelector}> 
                                <Calendar
                                    tooltip='Select the end date'
                                    className={styles.requestCalendars} 
                                    value={endDate} 
                                    minDate={startDate} 
                                    onChange={(e) => setEndDate(e.value)} 
                                    showTime
                                    stepMinute={10}
                                    hourFormat='12' 
                                    placeholder='To Date'
                                />

                            </div>
                        </div>
                        <div className={styles.requestsDropdownContainer}>
                            <div className={styles.requestsDropdowns}>
                                <Dropdown
                                    tooltip='Select the project'
                                    className={styles.requestsDropdownBox} 
                                    value={selectedProject}
                                    options={projects}
                                    onChange={(e) => setSelectedProject(e.value)}
                                    optionLabel='name'
                                    placeholder='Select a project'
                                />
                            </div>
                            
                            <div className={styles.requestsDropdowns}>
                                <Dropdown
                                    tooltip='Select the request reason'
                                    className={styles.requestsDropdownBox} 
                                    value={selectedReason}
                                    options={manualTimeReasonsLists}
                                    onChange={handleManualTimeRequestReason}
                                    optionValue='name'
                                    optionLabel='name'
                                    placeholder='Select a reason'
                                />
                            </div>
                        </div>
                        {explanationRequired && (
                            <div className={styles.manualTimeRequestExplanationInput}>
                                <div className="p-inputgroup flex-column flex-1">
                                    <label htmlFor="manual-time-request-explanation-input" className={styles.manualTimeRequestExplanationLabel}>Ticket number/Explanation *</label>
                                    <div className="p-inputgroup">
                                    <span className="p-inputgroup-addon"><i className="pi pi-info-circle" title='Please enter the ticket number or the request explanation'></i></span>
                                    <InputText
                                        id="manual-time-request-explanation-input"
                                        required
                                        maxLength={20}
                                        placeholder='Please enter the ticket number or the request explanation *'
                                        value={reasonExplanation}
                                        onChange={(e) => setReasonExplanation(e.target.value)}
                                    />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>

                        </div>
                        <div>

                        </div>
                    </div>
                </div>
            </Dialog>

            {/* OVERTIME DIALOG */}
            <Dialog 
                dismissableMask
                visible={requestOvertimeDialog}
                onHide={hideDialog}
                footer={requestOvertimeDialogFooter}
            >
                <div>
                    <h1>Overtime Request</h1>
                    <Message style={{marginBottom:"8px"}} severity='info' text={overtimeMessage}/>
                    <div className={styles.requestsForm}>
                        <div className={styles.requestsTimeSelectorContainer}>
                            <div className={styles.requestsTimeSelector}> 
                                <Calendar
                                    minDate={currentDate}
                                    tooltip='Select the start date'
                                    className={styles.requestCalendars} 
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.value)} 
                                    showTime 
                                    stepMinute={10}
                                    hourFormat='12' 
                                    placeholder='From Date'
                                />
                            </div>
                            <div className={styles.requestsTimeSelector}> 
                                <Calendar
                                    tooltip='Select the end date'
                                    className={styles.requestCalendars} 
                                    value={endDate} 
                                    minDate={startDate} 
                                    onChange={(e) => setEndDate(e.value)} 
                                    showTime 
                                    stepMinute={10}
                                    hourFormat='12' 
                                    placeholder='To Date'
                                />

                            </div>
                        </div>
                        <div className={styles.requestsDropdownContainer}>
                            <div className={styles.requestsDropdowns}>
                                <Dropdown
                                    tooltip='Select the project'
                                    className={styles.requestsDropdownBox} 
                                    value={selectedProject}
                                    options={projects}
                                    onChange={(e) => setSelectedProject(e.value)}
                                    optionLabel='name'
                                    placeholder='Select a project'
                                />
                            </div>
                            
                            <div className={styles.requestsDropdowns}>
                                <Dropdown
                                    tooltip='Select the request reason'
                                    className={styles.requestsDropdownBox} 
                                    value={selectedReason}
                                    options={overtimeReasonsLists}
                                    onChange={(e) => setSelectedReason(e.value)}
                                    optionValue='name'
                                    optionLabel='name'
                                    placeholder='Select a reason'
                                />

                            </div>
                        </div>

                        <div>

                        </div>
                        <div>

                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default Datatable;